import * as React from "react";
import { assetPath } from "@ewn/shared";
import {
  deleteAsset,
  listAssets,
  setAssetAlt,
  uploadAsset,
  type AdminAsset,
  type AdminLocale,
} from "@/api/admin";
import { useAction, useAsync } from "@/lib/useAsync";
import { useLocales } from "@/lib/useLocales";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Field, Input, Textarea } from "@/components/ui/input";
import { ConfirmModal, Modal } from "@/components/ui/modal";
import { Notice } from "@/components/ui/feedback";
import { PageHeader } from "@/components/ui/page-header";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * The images this shop serves, and the words that describe them.
 *
 * WEBP ONLY, AND THE WORKER SNIFFS THE BYTES. A file named `.webp` with a
 * JPEG inside is refused on the magic bytes rather than on its name, so nothing
 * here needs to pretend to check. This screen states the rule so an operator
 * knows to convert first; it does not enforce it, because a second enforcement
 * on this side is one that can disagree with the first.
 *
 * THE KEY IS THE IDENTITY OF THE BYTES. A public asset is served
 * `immutable` for a year, so changing a picture means a NEW key -- uploading
 * over an existing one leaves every browser that has seen it showing the old
 * image. The upload form says so where the decision is made.
 *
 * DIMENSIONS ARE READ HERE, in the browser, because this is the only side that
 * can: the worker would have to parse a WebP header to learn them. They are
 * sent with the upload and are what lets the storefront reserve the space
 * before the image arrives.
 */
export function AssetsScreen() {
  const { data, loading, error, reload } = useAsync(() => listAssets(), []);
  const localeList = useLocales();
  const [uploading, setUploading] = React.useState(false);
  const [editingAlt, setEditingAlt] = React.useState<AdminAsset | null>(null);
  const [removing, setRemoving] = React.useState<AdminAsset | null>(null);
  const action = useAction();

  const assets = data?.assets ?? [];
  const locales = localeList.locales;

  return (
    <div className="space-y-4">
      <PageHeader title="Images" description="Everything the storefront can point at.">
        <Button size="sm" onClick={() => setUploading(true)}>
          Upload an image
        </Button>
      </PageHeader>

      {error && <Notice tone="error">{error}</Notice>}
      {action.error && <Notice tone="error">{action.error}</Notice>}

      {loading && assets.length === 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-56" />
          ))}
        </div>
      ) : assets.length === 0 ? (
        <Card className="p-10 text-center text-sm text-muted-foreground">
          No images yet. WebP only — convert before uploading.
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {assets.map((asset) => (
            <AssetCard
              key={asset.key}
              asset={asset}
              locales={locales}
              onAlt={() => setEditingAlt(asset)}
              onDelete={() => setRemoving(asset)}
            />
          ))}
        </div>
      )}

      <UploadModal
        open={uploading}
        onClose={() => setUploading(false)}
        onUploaded={() => {
          setUploading(false);
          reload();
        }}
      />

      <AltModal
        asset={editingAlt}
        locales={locales}
        onClose={() => setEditingAlt(null)}
        onSaved={() => {
          setEditingAlt(null);
          reload();
        }}
      />

      <ConfirmModal
        open={removing !== null}
        title="Delete this image?"
        confirmLabel="Delete it"
        busy={action.busy}
        onClose={() => setRemoving(null)}
        body={
          <>
            <p>
              <span className="font-mono text-xs">{removing?.key}</span> is removed from storage as
              well as from this list.
            </p>
            <p className="mt-2">
              {/* The count lives in the DELETE itself, so this is a warning
                  about what will happen rather than a check made here. */}
              If a page or an article still points at it, the delete is refused and nothing is
              lost.
            </p>
          </>
        }
        onConfirm={() => {
          if (!removing) return;
          void action.run(() => deleteAsset(removing.key)).then((ok) => {
            setRemoving(null);
            if (ok) reload();
          });
        }}
      />
    </div>
  );
}

function AssetCard({
  asset,
  locales,
  onAlt,
  onDelete,
}: {
  asset: AdminAsset;
  locales: AdminLocale[];
  onAlt: () => void;
  onDelete: () => void;
}) {
  // Every locale the shop has, because alt text missing in one language is
  // exactly as invisible as alt text missing everywhere.
  const missing = locales.filter((l) => !asset.altByLocale[l.code]);

  return (
    <Card className="overflow-hidden">
      {/* The public route, same origin. The panel proxies `/api/*` to the
          worker, so this is the URL a visitor's browser would fetch too. */}
      <div className="flex h-40 items-center justify-center bg-muted">
        <img
          src={assetPath(asset.key)}
          alt=""
          className="max-h-40 max-w-full object-contain"
          loading="lazy"
        />
      </div>
      <div className="space-y-2 p-3">
        <div className="font-mono text-xs break-all">{asset.key}</div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>
            {asset.width ?? "?"}×{asset.height ?? "?"}
          </span>
          <span>·</span>
          <span>{Math.max(1, Math.round(asset.bytesLen / 1024))} KB</span>
          {missing.length > 0 && (
            <Badge color="amber">
              no alt in {missing.map((l) => l.code).join(", ")}
            </Badge>
          )}
        </div>
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline" onClick={onAlt}>
            Alt text
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete}>
            Delete
          </Button>
        </div>
      </div>
    </Card>
  );
}

/**
 * Reads an image's own dimensions, or gives up quietly.
 *
 * The worker treats width and height as optional, so a browser that cannot
 * decode the file still gets the upload through -- and the operator finds out
 * why from the worker's own refusal, which is about the bytes rather than
 * about this.
 */
function readDimensions(file: File): Promise<{ width: number | null; height: number | null }> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth || null, height: img.naturalHeight || null });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ width: null, height: null });
    };
    img.src = url;
  });
}

/** `Hero Image (2).WEBP` -> `hero-image-2.webp`, as a starting point. */
function suggestKey(name: string): string {
  const base = name.replace(/\.[^.]*$/, "");
  const slug = base
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 62);
  return slug ? `${slug}.webp` : "";
}

function UploadModal({
  open,
  onClose,
  onUploaded,
}: {
  open: boolean;
  onClose: () => void;
  onUploaded: () => void;
}) {
  const [file, setFile] = React.useState<File | null>(null);
  const [key, setKey] = React.useState("");
  const [touched, setTouched] = React.useState(false);
  const action = useAction();
  const reset = action.reset;

  React.useEffect(() => {
    if (!open) return;
    setFile(null);
    setKey("");
    setTouched(false);
    reset();
  }, [open, reset]);

  function pick(chosen: File | null) {
    setFile(chosen);
    // Only while the operator has not written their own: a suggestion that
    // overwrites a typed key turns choosing a different file into a silent
    // edit of the name.
    if (chosen && !touched) setKey(suggestKey(chosen.name));
  }

  async function submit() {
    if (!file || !key.trim()) return;
    const { width, height } = await readDimensions(file);
    const form = new FormData();
    form.set("key", key.trim());
    form.set("file", file);
    if (width) form.set("width", String(width));
    if (height) form.set("height", String(height));
    if (await action.run(() => uploadAsset(form))) onUploaded();
  }

  return (
    <Modal
      open={open}
      title="Upload an image"
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={action.busy}>
            Cancel
          </Button>
          <Button disabled={action.busy || !file || !key.trim()} onClick={() => void submit()}>
            {action.busy ? "Uploading…" : "Upload"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {action.error && <Notice tone="error">{action.error}</Notice>}

        <Field label="File" htmlFor="asset-file" hint="WebP only. Convert before uploading.">
          <Input
            id="asset-file"
            type="file"
            accept="image/webp"
            className="py-1.5"
            onChange={(e) => pick(e.target.files?.[0] ?? null)}
          />
        </Field>

        <Field
          label="Key"
          htmlFor="asset-key"
          hint="Lower-case letters, digits and dashes, ending in .webp."
        >
          <Input
            id="asset-key"
            className="font-mono text-xs"
            value={key}
            onChange={(e) => {
              setTouched(true);
              setKey(e.target.value);
            }}
          />
        </Field>

        <Notice tone="warning" title="Reusing a key replaces the picture, not the URL.">
          Images are cached for a year by the key, so browsers that have already seen this one keep
          showing the old picture. Use a new key when the picture changes.
        </Notice>
      </div>
    </Modal>
  );
}

function AltModal({
  asset,
  locales,
  onClose,
  onSaved,
}: {
  asset: AdminAsset | null;
  locales: AdminLocale[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [alt, setAlt] = React.useState<Record<string, string>>({});
  const action = useAction();
  const reset = action.reset;

  React.useEffect(() => {
    if (!asset) return;
    setAlt({ ...asset.altByLocale });
    reset();
  }, [asset, reset]);

  if (!asset) return null;

  async function submit() {
    if (!asset) return;
    // The whole map goes, every time: the worker replaces `alt_by_locale`
    // wholesale, so sending only what changed would delete the rest.
    if (await action.run(() => setAssetAlt(asset.key, alt))) onSaved();
  }

  return (
    <Modal
      open
      title="Alt text"
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={action.busy}>
            Cancel
          </Button>
          <Button disabled={action.busy} onClick={() => void submit()}>
            {action.busy ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {action.error && <Notice tone="error">{action.error}</Notice>}
        <p className="text-xs text-muted-foreground">
          What somebody hears instead of seeing <span className="font-mono">{asset.key}</span>.
          Leave a language empty if the image says nothing that the page does not.
        </p>
        {locales.map((locale) => (
          <Field key={locale.code} label={`${locale.name} (${locale.code})`} htmlFor={`alt-${locale.code}`}>
            <Textarea
              id={`alt-${locale.code}`}
              rows={2}
              value={alt[locale.code] ?? ""}
              onChange={(e) => setAlt((a) => ({ ...a, [locale.code]: e.target.value }))}
            />
          </Field>
        ))}
      </div>
    </Modal>
  );
}
